import React, { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import styled from 'styled-components';
import Button from 'src/components/form/Button';
import { useNavigate } from 'react-router';
import Meta from 'src/components/Meta';
import { PageContainer, Poppins, Spacer } from 'src/common';
import { useGaEventPageView } from '../../utils/gaEvent';
import { Main, Logo, Image, RightContainer } from './comps/styles';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import mobileHero from 'src/assets/mobileHero.png';

const Container = styled(PageContainer)`
  flex: 0.8;
  background-color: ${({ theme }) => theme.colors.white};

  .auth-contents {
    max-width: 610px;
    margin: auto;
    display: flex;
    flex-direction: column;
    height: 100%;

    &__laptop {
      max-width: 100%;
      display: none;
      margin-bottom: 30px;
    }

    &__buttons {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 18px;
      max-width: 400px;
      width: 100%;
    }

    &__footer {
      display: flex;
      margin-top: auto;
      text-align: left !important;
      padding-top: 20px;
    }
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    flex: 1;
    .auth-contents {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      padding-top: 54px;

      &__logo {
        width: 100px;
        margin-bottom: -12px;
      }

      &__title {
        font-size: 32px;
      }

      &__laptop {
        display: block;
      }
      &__buttons {
        gap: 9px;
      }

      span {
        text-align: center;
      }
    }
  }
`;

const StyledLink = styled.a<{ $footer?: boolean }>`
  color: ${({ theme, $footer }) => ($footer ? theme.colors.davysGrey : theme.colors.prussianBlue)};
  font-weight: 500;
`;

const AuthPage: React.FC = () => {
  const { loginWithRedirect, isAuthenticated, isLoading } = useAuth0();
  const navigate = useNavigate();

  useGaEventPageView('Cydea | Auth');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isLoading, isAuthenticated, navigate]);

  if (isLoading || isAuthenticated) {
    return null;
  }

  return (
    <>
      <Meta title={`Cydea`} />
      <Main id="main">
        <Container $noSideMargin>
          <div className="auth-contents">
            <Spacer $px={50} />
            <Logo className="auth-contents__logo" />
            <Poppins px={56} weight={600} className="auth-contents__title">
              Risk Platform
            </Poppins>
            <Spacer $px={10} className="in-text" />
            <img src={mobileHero} alt="" className="auth-contents__laptop" />

            <Poppins weight={600} px={20}>
              Track your risk, manage your programme, <br />
              and know what's next. All in one place.
            </Poppins>
            <Poppins color="cflowerBlue">Set it up in minutes - we'll guide you every step of the way.</Poppins>
            <Spacer $px={20} />
            <Poppins weight={600}>Try it free for 30 days</Poppins>
            <Poppins color="cflowerBlue">No credit card needed. Jump in and explore.</Poppins>

            <Spacer $px={24} />
            <div className="auth-contents__buttons">
              <Button
                type="button"
                onClick={() => {
                  loginWithRedirect({ authorizationParams: { prompt: 'login' } });
                  mpEvent(MPEvents.ButtonClick, {
                    button: 'Login',
                    tags: ['USER'],
                  });
                }}
                primary
                data-cy="signin-button"
              >
                Log in
              </Button>
              <Button
                type="button"
                onClick={() => {
                  loginWithRedirect({
                    authorizationParams: {
                      screen_hint: 'signup',
                    },
                  });
                  mpEvent(MPEvents.ButtonClick, {
                    button: 'Sign up',
                    tags: ['USER'],
                  });
                }}
                primary
                data-cy="signin-button"
              >
                Sign Up
              </Button>
            </div>

            <div className="auth-contents__footer">
              <Poppins px={14} weight={500} color="davysGrey">
                © {new Date().getFullYear()} Cydea Ltd. Registered in England and Wales, 12204451. Cydea is a
                registered trademark. Use is subject to our{' '}
                <StyledLink href="https://cydea.com/saas-terms/" target="_blank" $footer>
                  terms
                </StyledLink>
                . Read about your{' '}
                <StyledLink href="https://cydea.com/privacy/" target="_blank" $footer>
                  privacy & how we use data
                </StyledLink>
                .
              </Poppins>
            </div>
          </div>
        </Container>
        <RightContainer>
          <Image />
        </RightContainer>
      </Main>
    </>
  );
};

export default AuthPage;
